import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { FaTimes, FaTrash } from 'react-icons/fa';
import api from '../../services/api';
import UploadButton from '../../components/UploadButton';
import '../../css/financeiro-parcela-pagar.css';
import '../../css/testes-qa.css';

// Esta tela É o roteiro publicado como artifact, com o quadradinho de cada caso ligado
// ao banco. A estrutura e o vocabulário são os do artifact, sem nada por cima:
//
//   head (kicker · h1 · sub) → gate → block* → foot (commits)
//   block: num · title · tag · where · why · cases · notes
//   case:  id · do · expect · blocked
//
// O que o artifact não tem, esta tela não tem: nem KPI, nem barra de progresso, nem
// busca, nem filtro, nem cabeçalho fixo. O documento se lê de cima a baixo.
//
// O progresso vive em `qa_teste_status`, indexado por (bancoDados, roteiro, itemId,
// testador). `case.id` É o `itemId` — nunca renomeie o de um caso já testado.
const LS_TESTADOR = 'qa-testador-atual';

// A lista de testadores é a identidade do painel inteiro, não o elenco de uma entrega:
// ela só CRESCE. Tirar alguém daqui deixa inalcançável tudo que a pessoa já marcou.
const TESTADORES = [
    { id: 'rafael', nome: 'Rafael' },
    { id: 'otavio', nome: 'Otávio' },
    { id: 'joao', nome: 'João' },
    { id: 'meg', nome: 'Meg' },
];

const STATUS = [
    { id: 'ok', rotulo: 'Passou' },
    { id: 'falhou', rotulo: 'Falhou' },
    { id: 'testando', rotulo: 'Testando' },
    { id: 'info', rotulo: 'Informações' },
    { id: 'na', rotulo: 'N/A' },
];

function esc(t) {
    return String(t).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
// O artifact marca valor exato com <code class="v"> e ênfase com <b>. É só isso.
function inlineHtml(t) {
    return esc(t)
        .replace(/\*\*([^*]+)\*\*/g, '<b>$1</b>')
        .replace(/`([^`]+)`/g, '<code class="v">$1</code>');
}
function Md({ text, tag = 'span', className }) {
    const Tag = tag;
    return <Tag className={className} dangerouslySetInnerHTML={{ __html: inlineHtml(text || '') }} />;
}

export default function PainelTestesQA() {
    const token = localStorage.getItem('token');
    const [testador, setTestador] = useState(() => localStorage.getItem(LS_TESTADOR) || '');
    const [progresso, setProgresso] = useState({});
    const [erro, setErro] = useState('');

    const [roteiros, setRoteiros] = useState([]);
    const [slug, setSlug] = useState('');
    const [roteiro, setRoteiro] = useState(null);

    useEffect(() => {
        api({ method: 'get', action: '/qaRoteiro', token })
            .then((res) => {
                const lista = Array.isArray(res?.data?.data) ? res.data.data : [];
                if (!lista.length) throw new Error('nenhum roteiro cadastrado');
                setRoteiros(lista);
                // Só entra direto quando o link pediu um roteiro (`?roteiro=<slug>`, que é o
                // que a mensagem de deploy manda). Sem isso, cai na escolha.
                const pedido = new URLSearchParams(window.location.search).get('roteiro');
                if (lista.some((r) => r.slug === pedido)) setSlug(pedido);
            })
            .catch((e) => setErro(e.message || 'não consegui carregar os roteiros'));
    }, [token]);

    useEffect(() => {
        if (!slug) return;
        setRoteiro(null);
        setErro('');
        api({ method: 'get', action: '/qaRoteiro/' + slug, token })
            .then((res) => {
                if (!res?.data?.data) throw new Error('roteiro não encontrado: ' + slug);
                setRoteiro(res.data.data);
            })
            .catch((e) => setErro(e.message || 'não consegui carregar o roteiro'));
    }, [slug, token]);

    // A barra de endereço sempre reflete o roteiro aberto — é dela que sai o link que vai
    // para a mensagem de deploy. Sem isto, quem entra pela tela de escolha fica com
    // `/qaTestes` pelado e não tem o que copiar.
    useEffect(() => {
        if (!slug) return;
        const naUrl = new URLSearchParams(window.location.search).get('roteiro');
        if (naUrl !== slug) {
            window.history.replaceState(null, '', window.location.pathname + '?roteiro=' + slug);
        }
    }, [slug]);

    // O progresso segue o roteiro: sem `slug` nas dependências, a busca sairia com o
    // valor do primeiro render (vazio) e nada voltaria para a tela.
    const carregarProgresso = useCallback((quem) => {
        if (!quem || !slug) return;
        api({ method: 'get', action: '/qaTesteStatus', token, params: { roteiro: slug, testador: quem } })
            .then((res) => {
                const linhas = Array.isArray(res?.data?.data) ? res.data.data : [];
                const mapa = {};
                linhas.forEach((l) => {
                    mapa[l.itemId] = { status: l.status, observacao: l.observacao || '', arquivoId: l.arquivoId || null };
                });
                setProgresso(mapa);
            })
            .catch(() => toast.error('Erro ao carregar o progresso salvo.'));
    }, [token, slug]);

    useEffect(() => {
        if (testador) carregarProgresso(testador);
    }, [testador, carregarProgresso]);

    const escolherTestador = (id) => {
        localStorage.setItem(LS_TESTADOR, id);
        setTestador(id);
    };
    const trocarTestador = () => {
        localStorage.removeItem(LS_TESTADOR);
        setTestador('');
        setProgresso({});
    };
    const trocarRoteiro = () => {
        setSlug('');
        setRoteiro(null);
        setProgresso({});
        // tira o ?roteiro= da URL, senão um F5 volta direto para o roteiro que acabou de sair
        window.history.replaceState(null, '', window.location.pathname);
    };

    const statusDe = (id) => (progresso[id] && progresso[id].status) || 'pendente';

    const persistir = (id, campos) => {
        const atual = progresso[id] || {};
        api({
            method: 'post',
            action: '/qaTesteStatus',
            token,
            data: {
                roteiro: slug,
                itemId: id,
                testador,
                status: campos.status ?? atual.status ?? 'pendente',
                observacao: campos.observacao ?? atual.observacao ?? '',
            },
        }).catch(() => toast.error('Erro ao salvar — tente novamente.'));
    };

    const marcar = (id, statusId) => {
        const novo = statusDe(id) === statusId ? 'pendente' : statusId;
        setProgresso((p) => ({ ...p, [id]: { ...(p[id] || {}), status: novo } }));
        persistir(id, { status: novo });
    };
    const anotar = (id, texto) => {
        setProgresso((p) => ({ ...p, [id]: { ...(p[id] || {}), observacao: texto } }));
        persistir(id, { observacao: texto });
    };
    const printEnviado = (id, registro) => {
        if (!registro || !registro.arquivoId) { toast.error('Erro ao enviar print.'); return; }
        setProgresso((p) => ({ ...p, [id]: { ...(p[id] || {}), arquivoId: registro.arquivoId } }));
        toast.success('Print enviado.');
    };
    const excluirPrint = (id) => {
        api({ method: 'post', action: '/qaTesteStatusArquivoExcluir', token, data: { roteiro: slug, itemId: id, testador } })
            .then(() => {
                setProgresso((p) => ({ ...p, [id]: { ...(p[id] || {}), arquivoId: null } }));
                toast.success('Print removido.');
            })
            .catch(() => toast.error('Erro ao remover print.'));
    };

    // ── Quem está testando ──
    if (!testador) {
        return (
            <Escolha titulo="Testes QA — quem é você?" ajuda="Selecione seu nome para registrar os testes separadamente.">
                {TESTADORES.map((t) => (
                    <button key={t.id} type="button" onClick={() => escolherTestador(t.id)}>{t.nome}</button>
                ))}
            </Escolha>
        );
    }

    if (erro) {
        return <Escolha titulo="Não consegui carregar" ajuda={erro} />;
    }

    // ── Qual roteiro ──
    if (!slug) {
        return (
            <Escolha
                titulo="Qual roteiro você vai testar?"
                ajuda="Cada roteiro guarda o progresso separado — o que você marcar aqui não aparece nos outros."
            >
                {roteiros.length === 0
                    ? <p className="carregando">Carregando roteiros...</p>
                    : roteiros.map((r) => (
                        <button key={r.slug} type="button" onClick={() => setSlug(r.slug)}>
                            {r.titulo || r.title}
                            <small>{r.data}{r.situacao === 'arquivado' ? ' · arquivado' : ''}</small>
                        </button>
                    ))}
            </Escolha>
        );
    }

    if (!roteiro) {
        return <Escolha titulo="Carregando roteiro..." ajuda="" />;
    }

    const { gate, blocks = [], commits = [] } = roteiro.conteudo || {};
    const nomeTestador = TESTADORES.find((t) => t.id === testador)?.nome;

    return (
        <div className="qa-roteiro">
            <div className="wrap">
                <header className="head">
                    {roteiro.kicker && <p className="kicker">{roteiro.kicker}</p>}
                    <h1>{roteiro.title || roteiro.titulo}</h1>
                    {roteiro.sub && <p className="sub">{roteiro.sub}</p>}
                </header>

                <p className="quem">
                    Testando como <b>{nomeTestador}</b>
                    <button type="button" onClick={trocarTestador}>trocar</button>
                    {roteiros.length > 1 && (
                        <>
                            {' · '}
                            <button type="button" onClick={trocarRoteiro}>outro roteiro</button>
                        </>
                    )}
                </p>

                {gate && (
                    <div className="gate">
                        {gate.title && <h2>{gate.title}</h2>}
                        {(gate.paragraphs || []).map((p, i) => <Md key={i} tag="p" text={p} />)}
                    </div>
                )}

                {blocks.map((block) => {
                    const wheres = block.wheres || [];
                    const umLugarSo = wheres.length === 1;

                    return (
                        <section className="block" key={block.num}>
                            <div className="block-head">
                                <span className="num">{block.num}</span>
                                <Md tag="h2" text={block.title} />
                                {block.tag && <span className={'tag ' + block.tag.kind}>{block.tag.text}</span>}
                            </div>

                            {/* No artifact a ordem é where → why. Com mais de um lugar, o why
                                é do bloco inteiro e vem antes do primeiro where. */}
                            {umLugarSo && <Where dados={wheres[0]} />}
                            {block.why && <Md tag="p" className="why" text={block.why} />}

                            {wheres.map((w, i) => (
                                <React.Fragment key={i}>
                                    {(!umLugarSo || i > 0) && <Where dados={w} />}
                                    <ol className="cases">
                                        {(w.cases || []).map((caso) => (
                                            <Caso
                                                key={caso.id}
                                                caso={caso}
                                                status={statusDe(caso.id)}
                                                dados={progresso[caso.id] || {}}
                                                testador={testador}
                                                roteiro={slug}
                                                onMarcar={(s) => marcar(caso.id, s)}
                                                onAnotar={(t) => anotar(caso.id, t)}
                                                onPrint={(r) => printEnviado(caso.id, r)}
                                                onExcluirPrint={() => excluirPrint(caso.id)}
                                            />
                                        ))}
                                    </ol>
                                </React.Fragment>
                            ))}

                            {(block.notes || []).map((n, i) => <Md key={i} tag="p" className="note" text={n} />)}
                        </section>
                    );
                })}

                <footer className="foot">
                    <p>
                        Ao reportar qualquer erro, mande <b>print + URL completa + o que você fez antes</b> —
                        sem isso não dá para reproduzir.
                    </p>
                    {commits.length > 0 && (
                        <table className="commits">
                            <tbody>
                                {commits.map((c, i) => (
                                    <tr key={i}>
                                        <td>{c.sha}</td>
                                        <td><Md text={c.text} /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </footer>
            </div>
        </div>
    );
}

function Escolha({ titulo, ajuda, children }) {
    return (
        <div className="qa-roteiro">
            <div className="wrap">
                <div className="escolha">
                    <h2>{titulo}</h2>
                    {ajuda && <p>{ajuda}</p>}
                    {children && <div className="opcoes">{children}</div>}
                </div>
            </div>
        </div>
    );
}

function Where({ dados }) {
    if (!dados || (!dados.code && !dados.then)) return null;
    return (
        <div className="where">
            <span className="lbl">{dados.lbl || 'Onde'}</span>
            {dados.code && <code>{dados.code}</code>}
            {dados.then && <Md className="then" text={dados.then} />}
        </div>
    );
}

// O quadradinho do artifact, ligado ao banco: mostra o estado e abre as opções.
function Caso({ caso, status, dados, testador, roteiro, onMarcar, onAnotar, onPrint, onExcluirPrint }) {
    const [aberto, setAberto] = useState(false);
    const [obs, setObs] = useState(dados.observacao || '');
    const [verPrint, setVerPrint] = useState(false);
    const ref = useRef(null);

    useEffect(() => { setObs(dados.observacao || ''); }, [dados.observacao]);

    useEffect(() => {
        if (!aberto) return undefined;
        const fora = (e) => { if (ref.current && !ref.current.contains(e.target)) setAberto(false); };
        document.addEventListener('mousedown', fora);
        return () => document.removeEventListener('mousedown', fora);
    }, [aberto]);

    const marcado = status !== 'pendente';
    const rotulo = STATUS.find((s) => s.id === status)?.rotulo;

    return (
        <li className={'st-' + status}>
            <div className="marca" ref={ref}>
                <button
                    type="button"
                    className="box"
                    title={marcado ? rotulo : 'Marcar'}
                    aria-label={'Caso ' + caso.id + (marcado ? ' — ' + rotulo : '')}
                    onClick={() => setAberto((a) => !a)}
                />
                {aberto && (
                    <div className="opcoes-status">
                        {STATUS.map((s) => (
                            <button
                                key={s.id}
                                type="button"
                                className={'op-' + s.id + (status === s.id ? ' ativo' : '')}
                                onClick={() => { onMarcar(s.id); setAberto(false); }}
                            >
                                {s.rotulo}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            <div>
                {(caso.extras || []).map((e, i) => (
                    <p className="extra" key={i}><b>{e.lbl}</b> <Md text={e.text} /></p>
                ))}
                {caso.do && <Md tag="p" className="do" text={caso.do} />}
                {caso.expect && (
                    <p className={'expect' + (caso.blocked ? ' block-red' : '')}>
                        <Md text={caso.expect} />
                    </p>
                )}

                <div className="anotacao">
                    <input
                        type="text"
                        placeholder="Observações..."
                        value={obs}
                        onChange={(e) => setObs(e.target.value)}
                        onBlur={() => { if (obs !== (dados.observacao || '')) onAnotar(obs); }}
                    />
                    <UploadButton
                        small
                        title="Anexar print"
                        acaoApi="qaTesteStatusArquivo"
                        dados={{ roteiro, itemId: caso.id, testador }}
                        callBack={onPrint}
                    >
                        Print
                    </UploadButton>
                    {dados.arquivoId && (
                        <img
                            className="thumb"
                            src={`${process.env.REACT_APP_API_URL}/arquivo/download/${dados.arquivoId}?inline=1`}
                            alt={`Print do caso ${caso.id}`}
                            title="Ver print"
                            onClick={() => setVerPrint(true)}
                        />
                    )}
                </div>
            </div>

            {verPrint && dados.arquivoId && (
                <div
                    className="modal fade show d-block fin-pp fin-pp__modalbg"
                    onClick={(e) => { if (e.target === e.currentTarget) setVerPrint(false); }}
                >
                    <div className="modal-dialog modal-dialog-centered modal-lg">
                        <div className="modal-content fin-pp__modal">
                            <div className="fin-pp__modal-head">
                                <h3 className="fin-pp__modal-title">Print do caso {caso.id}</h3>
                                <button type="button" className="fin-pp__modal-close" onClick={() => setVerPrint(false)}>
                                    <FaTimes size={16} />
                                </button>
                            </div>
                            <div className="modal-body">
                                <img
                                    style={{ width: '100%' }}
                                    src={`${process.env.REACT_APP_API_URL}/arquivo/download/${dados.arquivoId}?inline=1`}
                                    alt={`Print do caso ${caso.id}`}
                                />
                            </div>
                            <div className="fin-pp__modal-foot">
                                <button
                                    type="button"
                                    className="fin-pp__btn fin-pp__btn--danger"
                                    onClick={() => { setVerPrint(false); onExcluirPrint(); }}
                                >
                                    <FaTrash size={13} /> Remover print
                                </button>
                                <button type="button" className="fin-pp__btn" onClick={() => setVerPrint(false)}>
                                    Fechar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </li>
    );
}
